import React, { useCallback, useState } from 'react';
import RotatedMedia from './RotatedMedia';
import VideoControlBar from './VideoControlBar';
import VideoPlayer from './VideoPlayer';
import { normalizeRotation } from '../videoUpload/rotation';

// The player, for every surface in the app: source footage on Footage,
// Overview and the editor, and the finished render on the result panel. One
// component so the product has ONE player, and the bar under it is the app's
// own rather than the browser's.
//
// It was previously `RotatedVideoPlayer`, and only rotated projects got the
// custom bar. The reason for the bar is still rotation-shaped: a CSS transform
// turns the ENTIRE element, and a <video> carries the browser's control bar
// inside its own user-agent shadow DOM, so rotating the picture rotates the
// play button and scrubber with it — vertically down one side at 90/270, and
// at 180 upside down along the top with the scrubber's drag direction
// reversed. That lands on the exact surface where the user is checking, by eye,
// whether the rotation they just applied is right.
//
// What changed is the scope of the answer. Applying it only when rotated left
// the product with two different-looking players, and which one you got
// depended on a project field most users never touch. So the native controls
// are off everywhere and this bar is on everywhere; a rotation of 0 is now just
// the case where the picture happens not to turn.
//
// The structure is the contract: the bar is a SIBLING of the rotated frame,
// inside the stage box. Move it inside RotatedMedia and it turns with the
// picture, which is the bug this whole file exists to avoid.
const VideoStage = React.forwardRef(({
  src,
  rotation = 0,
  // Source pixel dimensions — only used to shape the frame on a quarter turn.
  width = null,
  height = null,
  onPlay,
  onError,
  onTimeUpdate,
  // Optional in-bar rotate segment. Only source-footage surfaces that can
  // persist the change pass these; the rendered result must not, because
  // FFmpeg has already baked the rotation into those pixels.
  onRotate = null,
  rotateDisabled = false,
  // Drawn over the picture, inside the rotation frame and above the video —
  // MotionPace's speed-dial preview (#533). Passed straight through to
  // RotatedMedia, which is where the reasoning about why it must not turn with
  // the picture lives.
  overlay = null,
}, ref) => {
  const angle = normalizeRotation(rotation);

  // Held as state, not refs, because the control bar has to re-render (and
  // re-subscribe) when these elements arrive. A ref assignment alone would
  // leave the bar mounted against `null` and dead.
  const [videoEl, setVideoEl] = useState(null);
  const [stageEl, setStageEl] = useState(null);

  // The <video> node is the ref's payload for callers too: MotionTimeline
  // seeks by writing `currentTime` on it, so the same node has to reach both
  // the parent's ref and the bar below.
  const attach = useCallback((node) => {
    setVideoEl(node);
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  }, [ref]);

  return (
    <div
      className="tw-stage"
      ref={setStageEl}
      data-testid="video-stage"
      // Focusable, but deliberately NOT a tab stop: clicking the picture has to
      // put focus somewhere for the player's keyboard shortcuts to reach it,
      // while tabbing through the page should land on the controls themselves.
      tabIndex={-1}
    >
      <RotatedMedia rotation={angle} width={width} height={height} overlay={overlay}>
        <VideoPlayer
          src={src}
          ref={attach}
          onPlay={onPlay}
          onError={onError}
          onTimeUpdate={onTimeUpdate}
        />
      </RotatedMedia>
      <VideoControlBar
        video={videoEl}
        container={stageEl}
        rotation={angle}
        onRotate={onRotate}
        rotateDisabled={rotateDisabled}
      />
    </div>
  );
});

export default VideoStage;
